/**
 * The five recruiter message types that mean the application moved (NEXT-STAGE-PLAN
 * essential 5). The server decides which of them fires a push, a drafted reply and a prep
 * pack; this module exists so every screen in this client agrees on WHICH messages those
 * are and what they are called, instead of each one re-deriving it from a substring.
 *
 * The literal strings are flash-collection's `EmailMessageType` values, copied from
 * `NEXT_STAGE_MESSAGE_TYPES` in its `prepPack.ts`. The match is the whole string, not the
 * bracketed tag, because that is exactly what the server's `isNextStageMessage` compares —
 * a looser match here would put a Reply button on a message the reply route answers 400 to,
 * which is a worse failure than not offering the button at all.
 */

// tag -> what the user sees. One label per stage, used by the mailbox, the job detail
// header and the prep tab, so a stage never reads as two different things.
export const NEXT_STAGE_LABELS = {
  "PHONE INTERVIEW": "Phone screen",
  INTERVIEW: "Interview",
  "ONLINE ASSESSMENT": "Online assessment",
  OFFER: "Offer",
  ACTION: "Action needed",
};

// The canonical messageType strings, for fixtures and for anything that needs the full
// value rather than the tag.
export const NEXT_STAGE_MESSAGE_TYPES = [
  "[PHONE INTERVIEW] Phone Interview",
  "[INTERVIEW] Interview",
  "[ONLINE ASSESSMENT] Online Assessment",
  "[OFFER] Offer",
  "[ACTION] Additional Information Required",
];

/** The bracketed tag of a messageType, upper-cased and whitespace-collapsed, or "". */
export function nextStageTag(messageType) {
  if (typeof messageType !== "string") return "";
  const match = messageType.match(/^\s*\[([^\]]+)\]/);
  if (!match) return "";
  return match[1].trim().replace(/\s+/g, " ").toUpperCase();
}

// Every other kind of recruiter mail the extractor writes, and the kind of next step it gets. Matched
// on the whole stored string, like the five above. Security codes and promotions get none.
const OTHER_MESSAGE_KINDS = {
  "[BACKGROUND CHECK] Background Check": "backgroundCheck",
  "[SURVEY] Survey": "survey",
  "[REMINDER] Reminder": "reminder",
  "[CONFIRMATION] Application Received": "received",
  "[REJECTION] Application Rejected": "notSelected",
  "[WITHDRAWAL] Application Withdrawn": "withdrawn",
  "[OTHER] Other": "other",
};

/** True when this message is one of the five stages the next-stage work responds to. */
export function isNextStageMessage(messageType) {
  return NEXT_STAGE_MESSAGE_TYPES.includes(String(messageType ?? ""));
}

/** The label for a stage, or "" when the message is not a next-stage message. */
export function nextStageLabel(messageType) {
  if (!isNextStageMessage(messageType)) return "";
  return NEXT_STAGE_LABELS[nextStageTag(messageType)] || "";
}

/**
 * A stored messageType as words: the stage label for the five next-stage types, otherwise the
 * part after the bracketed tag ("[REJECTION] Application Rejected" reads "Application Rejected").
 */
export function messageTypeWords(messageType) {
  const label = nextStageLabel(messageType);
  if (label) return label;
  return typeof messageType === "string" ? messageType.trim().replace(/^\[[^\]]*\]\s*/, "") : "";
}

// What an email's pipeline status means to the person reading it. "failed" is the extractor's
// marker for an email it could not read, and archived/pendingDecision are never written by mail,
// so those (and anything unknown) say nothing rather than something wrong.
const STATUS_WORDS = {
  applying: "Action to finish applying",
  applied: "Application received",
  rejected: "Not moving forward",
  screen: "Screening",
  interview: "Interview",
  offer: "Offer",
};

/** An email row's `status` as words ("screen" reads "Screening"), or "" when it says nothing useful. */
export function statusWords(status) {
  return STATUS_WORDS[String(status ?? "").trim().toLowerCase()] || "";
}

/**
 * What the user has to do next (the same table iOS keeps as `NextStep`). A recruiter's message
 * decides it when we have one; otherwise the stage the job sits in does. `short` is for cards and
 * list rows ("Next: …"), `long` for the "What to do next" boxes.
 */
const NEXT_STEP_TEXT = {
  phoneScreen: {
    short: "Pick a time for the call",
    long: "Pick a time for a short call with the recruiter. Reply to their email, or use the scheduling link in it.",
  },
  assessment: {
    short: "Complete the assessment",
    long: "Take the online assessment from the link in their email. It works best on a laptop or desktop computer.",
  },
  interview: {
    short: "Confirm your interview time",
    long: "Confirm a time for your interview. Reply to their email, or use the scheduling link in it.",
  },
  offer: {
    short: "Review your offer",
    long: "Read the offer, then reply to the recruiter with your questions or your decision.",
  },
  action: {
    short: "Send what they asked for",
    long: "The recruiter needs more information from you. Their email says what to send.",
  },
  screenStage: {
    short: "Check the recruiter's email",
    long: "You're in screening. Open the recruiter's email to see what they need from you.",
  },
  interviewStage: {
    short: "Check the recruiter's email",
    long: "You're in the interview stage. Open the recruiter's email for the time and details.",
  },
  offerStage: {
    short: "Check the recruiter's email",
    long: "You have an offer. Open the recruiter's email for the details.",
  },
  backgroundCheck: {
    label: "Background check",
    short: "Complete the background check",
    long: "They started a background check. Fill in the form from their email (it often comes from a service like Checkr or Sterling) so your offer can move ahead.",
  },
  survey: {
    label: "Survey",
    short: "Fill in their survey",
    long: "They sent a survey. Fill it in if they say it's needed to move forward. Otherwise it's optional.",
  },
  reminder: {
    label: "Reminder",
    short: "See what's due",
    long: "They sent a reminder. Open their email to see what's due and when.",
  },
  received: {
    label: "Application received",
    short: "Nothing to do yet",
    long: "They got your application. Nothing to do yet. Replies usually take one to three weeks and will show up here.",
  },
  notSelected: {
    label: "Not moving forward",
    short: "No action needed",
    long: "They're moving forward with other candidates. Nothing to do here. We've moved this job to your archive.",
  },
  withdrawn: {
    label: "Application withdrawn",
    short: "No action needed",
    long: "This application was withdrawn. Nothing to do here.",
  },
  other: {
    label: "Message",
    short: "Read their message",
    long: "Read their message to see if they need anything from you.",
  },
};

// Kinds that only say where things stand: there is nothing for the user to do.
const INFO_KINDS = new Set(["received", "notSelected", "withdrawn", "other"]);
// A job that is closed: only the mail that closed it still says anything.
const CLOSED_STAGES = new Set(["archived", "failed", "rejected"]);
const CLOSING_KINDS = new Set(["notSelected", "withdrawn"]);

const KIND_BY_TAG = {
  "PHONE INTERVIEW": "phoneScreen",
  "ONLINE ASSESSMENT": "assessment",
  INTERVIEW: "interview",
  OFFER: "offer",
  ACTION: "action",
};
const STAGE_KINDS = {
  screen: { kind: "screenStage", label: "Screening" },
  interview: { kind: "interviewStage", label: "Interview" },
  offer: { kind: "offerStage", label: "Offer" },
};
// A message about an earlier stage than the job is in now is history, not a next step: a phone
// screen invite on a job the user has moved to Interview. ACTION can arrive at any stage.
const STAGE_RANK = { applied: 1, screen: 2, interview: 3, offer: 4 };
const MESSAGE_RANK = { received: 1, phoneScreen: 2, assessment: 2, interview: 3, offer: 4, backgroundCheck: 4 };

/** The kind of next step a stored messageType gets, or undefined (security codes, promotions, unknown). */
export function messageKind(messageType) {
  if (isNextStageMessage(messageType)) return KIND_BY_TAG[nextStageTag(messageType)];
  return OTHER_MESSAGE_KINDS[String(messageType ?? "")];
}

/**
 * `{ kind, label, short, long, fromMessage, needsAction }` for a message type and/or a pipeline stage,
 * or null when neither calls for anything (an "applied" job with no recruiter message, or one that is
 * closed, unless it is the mail that closed it). `needsAction` is false for the kinds that only say
 * where things stand ("Application received", "Not moving forward").
 */
export function nextStepFor({ messageType, stage } = {}) {
  const kind = messageKind(messageType);
  if (CLOSED_STAGES.has(stage) && !CLOSING_KINDS.has(kind)) return null;
  const stale = !!kind && (STAGE_RANK[stage] ?? 0) > (MESSAGE_RANK[kind] ?? Infinity);
  if (kind && !stale) {
    const text = NEXT_STEP_TEXT[kind];
    return { kind, label: nextStageLabel(messageType) || text.label, fromMessage: true, needsAction: !INFO_KINDS.has(kind), short: text.short, long: text.long };
  }
  const byStage = STAGE_KINDS[stage];
  return byStage ? { ...byStage, fromMessage: false, needsAction: true, ...NEXT_STEP_TEXT[byStage.kind] } : null;
}

/**
 * What a message is, in the words the next-step boxes use ("Survey", "Background check",
 * "Not moving forward"), or "" for mail that has no kind of its own. Rows name mail by this rather
 * than by its stored status, which says where the job was, not what the mail is.
 */
export function messageLabel(messageType) {
  const kind = messageKind(messageType);
  if (!kind || kind === "other") return "";
  return nextStageLabel(messageType) || NEXT_STEP_TEXT[kind]?.label || "";
}

/** How a row says a step: "Next: Pick a time for the call", or just "Nothing to do yet" when there is nothing to do. */
export function nextStepLine(step) {
  if (!step) return "";
  return step.needsAction === false ? step.short : `Next: ${step.short}`;
}

/**
 * The next step an EMAIL carries, or null. An email speaks for its own message only: once its job
 * has moved past it, or was archived or failed, it shows no next step at all rather than the text
 * for the job's stage, which belongs on the job.
 */
export function emailNextStep({ messageType, stage } = {}) {
  const step = nextStepFor({ messageType, stage });
  return step?.fromMessage ? step : null;
}

// The mail a My Jobs card points at: the five next stages, and a background check on an offer.
const isCardMessage = (messageType) => isNextStageMessage(messageType) || messageKind(messageType) === "backgroundCheck";

/** A job url with one trailing "/" removed, the form job urls are compared in. */
export const withoutTrailingSlash = (url) => String(url ?? "").trim().replace(/\/$/, "");
const instantOf = (value) => toStageDate(value)?.getTime() ?? 0;

/**
 * The recruiter message a My Jobs card is about: the server's `nextStage` when it sent one (only
 * while reply is switched on), else the newest next-stage email in the mailbox's first page that
 * was matched to this job. Promotions and other kinds of mail never count.
 */
export function jobMessage(job, recentEmails) {
  const server = job?.nextStage;
  if (server && typeof server === "object" && server.emailId) return { ...server, emailId: String(server.emailId) };
  const jobUrl = withoutTrailingSlash(job?.jobUrl);
  if (!jobUrl || !Array.isArray(recentEmails)) return null;
  let newest = null;
  for (const email of recentEmails) {
    if (!email?.emailId || email.isPromotional || !isCardMessage(email.messageType)) continue;
    if (withoutTrailingSlash(email.jobUrl) !== jobUrl) continue;
    if (!newest || instantOf(email.timestamp) > instantOf(newest.timestamp)) newest = email;
  }
  if (!newest) return null;
  return {
    emailId: String(newest.emailId),
    messageType: newest.messageType,
    receivedAt: newest.timestamp,
    ...(newest.reply ? { reply: newest.reply } : {}),
    ...(newest.repliedAt ? { repliedAt: newest.repliedAt } : {}),
  };
}

/** The newest email in `recentEmails` matched to this job, of any kind but promotional: what Open Mailbox opens. */
export function newestJobEmail(job, recentEmails) {
  const jobUrl = withoutTrailingSlash(job?.jobUrl);
  if (!jobUrl || !Array.isArray(recentEmails)) return null;
  let newest = null;
  for (const email of recentEmails) {
    if (!email?.emailId || email.isPromotional || withoutTrailingSlash(email.jobUrl) !== jobUrl) continue;
    if (!newest || instantOf(email.timestamp) > instantOf(newest.timestamp)) newest = email;
  }
  return newest;
}

/**
 * Whether a Next steps item is the one the user cleared, with nothing new since: the job is still at the
 * stage it was cleared at, and its message is the same one or not newer than the clear (`at`, epoch ms).
 * A stage change or a newer message brings it back. `cleared` is the server's `upNextCleared`, keyed by
 * job URL without its trailing slash. iOS has the same rule (UpNextClear.hides).
 */
export function isClearedFromNextSteps(job, stage, message, cleared) {
  const entry = cleared?.[withoutTrailingSlash(job?.jobUrl)];
  if (!entry || entry.stage !== stage) return false;
  if (!message?.emailId || String(message.emailId) === String(entry.emailId)) return true;
  const received = toStageDate(message.receivedAt)?.getTime();
  return !received || received <= entry.at;
}

const NEXT_STEP_STAGES = ["applied", "screen", "interview", "offer"];

/**
 * Everything a job card says about its next step, or null for a card that has none:
 * - `step`: nextStepFor's answer.
 * - `message`: the recruiter message it came from, when there is one (see jobMessage).
 * - `action`: the one thing the card offers. "reply" (the server says a reply can go out),
 *   "open" (open that email), "mailbox" (no message to point at: open the Mailbox), or
 *   "replied" (the user already answered; the card says so and offers the email).
 */
export function jobNextStep(job, stage, recentEmails) {
  if (!NEXT_STEP_STAGES.includes(stage)) return null;
  const message = jobMessage(job, recentEmails);
  const step = nextStepFor({ messageType: message?.messageType, stage });
  if (!step) return null;
  let action = "mailbox";
  if (step.fromMessage && message?.repliedAt) action = "replied";
  else if (step.fromMessage && message?.reply?.available) action = "reply";
  else if (step.fromMessage && message) action = "open";
  return { step, message: step.fromMessage ? message : null, action };
}

/**
 * A server timestamp as a Date, or null, for the next-stage fields that are INSTANTS:
 * `message.receivedAt`, `prepPackFor.receivedAt` and `repliedAt` arrive as ISO strings. Epoch
 * numbers are tolerated too — the mail rows elsewhere carry epoch SECONDS as a numeric string,
 * and an earlier cut of the prep-pack route sent epoch milliseconds. A number or a numeric string
 * below 1e12 is read as seconds (1e12 ms is 2001; 1e12 s is year 33658), anything larger as
 * milliseconds, and any other string goes through Date.
 *
 * Not for logistics times: a date-only `YYYY-MM-DD` deadline is a calendar day, and Date would
 * read it as UTC midnight and show the day before west of Greenwich. Those go through
 * formatStageInstant.
 */
export function toStageDate(value) {
  let parsed;
  if (typeof value === "number") {
    parsed = Number.isFinite(value) ? new Date(value < 1e12 ? value * 1000 : value) : null;
  } else if (typeof value === "string" && value.trim() !== "") {
    const trimmed = value.trim();
    parsed = /^\d+$/.test(trimmed) ? toStageDate(Number(trimmed)) : new Date(trimmed);
  } else {
    return null;
  }
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
}

// ICU puts a narrow no-break space before AM/PM in some versions; a plain space reads the same
// and keeps the text stable across runtimes.
const plainSpaces = (text) => text.replace(/[\u202f\u00a0]/g, " ");

const DATETIME_PARTS = { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
const DATE_PARTS = { weekday: "short", month: "short", day: "numeric" };

const yearIn = (date, timeZone) =>
  new Intl.DateTimeFormat("en-US", timeZone ? { year: "numeric", timeZone } : { year: "numeric" }).format(date);

/**
 * Every date in the next-stage screens names its year only when it is not this year, the same
 * rule iOS uses: "Sep 20" this year, "Sep 20, 2025" otherwise. "This year" is read in the zone
 * the date is shown in. The clock is Date.now(), so a test can hold it still.
 */
const formatIn = (date, parts, timeZone) => {
  const now = new Date(Date.now());
  const withYear = yearIn(date, timeZone) === yearIn(now, timeZone) ? parts : { ...parts, year: "numeric" };
  return plainSpaces(new Intl.DateTimeFormat("en-US", timeZone ? { ...withYear, timeZone } : withYear).format(date));
};

/** A calendar date in en-US, the one date style next-stage screens use: "Sep 24", or "Sep 24, 2025". */
export function stageDateLabel(date, { localTimeZone } = {}) {
  return formatIn(date, { month: "short", day: "numeric" }, localTimeZone);
}

/** "Received Mon, Sep 28, 10:00 AM" for a message's `receivedAt`, in the user's own zone, or "". */
export function receivedLabel(receivedAt, { localTimeZone } = {}) {
  const date = toStageDate(receivedAt);
  return date ? `Received ${formatIn(date, DATETIME_PARTS, localTimeZone)}` : "";
}

/**
 * The zones a recruiter's email can name, and how to show a time in each (REPLY-SPEC G1). FC
 * computes instants with the SAME table, so what the user sees is the wall-clock time the email
 * stated. Nothing outside it is guessed at: an abbreviation Intl happens to know ("IST" is three
 * different places) is not evidence of which one the recruiter meant.
 *
 * - Generic names are a place, so daylight time follows the calendar: "PT" in July is PDT.
 * - Specific abbreviations are the fixed offset they spell: "10:00 AM EST" in September is still
 *   10:00 AM EST (UTC-5), not New York's 11:00 AM EDT. Etc/GMT signs are inverted by POSIX:
 *   Etc/GMT+5 is UTC-5.
 */
const ZONE_TABLE = {
  ET: "America/New_York", EASTERN: "America/New_York",
  CT: "America/Chicago", CENTRAL: "America/Chicago",
  MT: "America/Denver", MOUNTAIN: "America/Denver",
  PT: "America/Los_Angeles", PACIFIC: "America/Los_Angeles",
  AKT: "America/Anchorage",
  HT: "Pacific/Honolulu",
  EST: "Etc/GMT+5", EDT: "Etc/GMT+4",
  CST: "Etc/GMT+6", CDT: "Etc/GMT+5",
  MST: "Etc/GMT+7", MDT: "Etc/GMT+6",
  PST: "Etc/GMT+8", PDT: "Etc/GMT+7",
  AKST: "Etc/GMT+9", AKDT: "Etc/GMT+8",
  HST: "Etc/GMT+10",
  UTC: "UTC", GMT: "UTC",
};

/**
 * The IANA zone for a zone as an email wrote it, or null when it is not in the table and is not
 * a Region/City name. Unrecognised zones are shown in the viewer's own time, labelled as such.
 */
export function resolveTimeZone(written) {
  if (typeof written !== "string" || written.trim() === "") return null;
  const zone = written.trim();
  const known = ZONE_TABLE[zone.toUpperCase()];
  if (known) return known;
  if (!zone.includes("/")) return null;
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone: zone }).resolvedOptions().timeZone;
  } catch (e) {
    return null;
  }
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A logistics time as a person reads it, and whether the email's own zone was used
 * (REPLY-SPEC E2):
 * - `YYYY-MM-DD` is a calendar day: "Wed, Sep 30", no time, no zone shift.
 * - An instant is shown in the zone the email stated, followed by that zone as written
 *   ("Mon, Sep 28, 10:00 AM PT"), so the user sees the time the recruiter wrote; when the zone
 *   is missing or unrecognised, in the user's own zone followed by "(your time)".
 * - Anything that does not parse is shown as written: a deadline can be a phrase, and guessing
 *   at a date we cannot read would be worse than showing what the recruiter said.
 */
export function formatStageInstant(value, writtenZone, { localTimeZone } = {}) {
  if (typeof value !== "string" || value.trim() === "") return { text: "", zoneUsed: false };
  const raw = value.trim();
  const day = DATE_ONLY.exec(raw);
  if (day) {
    const [y, m, d] = [Number(day[1]), Number(day[2]), Number(day[3])];
    const date = new Date(Date.UTC(y, m - 1, d));
    const real = date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
    if (!real) return { text: raw, zoneUsed: false };
    const thisYear = Number(yearIn(new Date(Date.now()), localTimeZone));
    const parts = y === thisYear ? DATE_PARTS : { ...DATE_PARTS, year: "numeric" };
    return { text: plainSpaces(new Intl.DateTimeFormat("en-US", { ...parts, timeZone: "UTC" }).format(date)), zoneUsed: false };
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return { text: raw, zoneUsed: false };
  const zone = resolveTimeZone(writtenZone);
  if (zone) return { text: `${formatIn(parsed, DATETIME_PARTS, zone)} ${writtenZone.trim()}`, zoneUsed: true };
  return { text: `${formatIn(parsed, DATETIME_PARTS, localTimeZone)} (your time)`, zoneUsed: false };
}

/** Just the text of formatStageInstant. */
export function formatStageTime(value, writtenZone, options) {
  return formatStageInstant(value, writtenZone, options).text;
}

const present = (v) => v !== null && v !== undefined && v !== "";

/**
 * The logistics rows the Prep tab and the composer both show, in one order and one wording.
 * Both times use the email's stated zone (one email, one zone); the separate "Time zone" row is
 * dropped when that zone was used to render a time, and kept when it could not be.
 */
export function logisticsRows(logistics, options) {
  if (!logistics || typeof logistics !== "object") return [];
  const zone = typeof logistics.timeZone === "string" ? logistics.timeZone : undefined;
  const when = present(logistics.proposedTime) ? formatStageInstant(String(logistics.proposedTime), zone, options) : null;
  const due = present(logistics.deadline) ? formatStageInstant(String(logistics.deadline), zone, options) : null;
  const zoneUsed = !!(when?.zoneUsed || due?.zoneUsed);
  const rows = [];
  if (when) rows.push({ key: "proposedTime", label: "When", text: when.text });
  if (present(logistics.timeZone) && !zoneUsed) rows.push({ key: "timeZone", label: "Time zone", text: String(logistics.timeZone) });
  if (present(logistics.durationMinutes)) rows.push({ key: "durationMinutes", label: "Length", text: `${logistics.durationMinutes} min` });
  if (present(logistics.recruiterName)) rows.push({ key: "recruiterName", label: "Who wrote", text: String(logistics.recruiterName) });
  if (due) rows.push({ key: "deadline", label: "Due", text: due.text });
  return rows;
}
